/**
 * App palette system. The base layout chrome reads CSS custom properties
 * (--sfp-primary, --sfp-sidebar, --sfp-canvas, plus derived hovers). This module
 * defines the theme shape, presets, and the apply/load/save plumbing so users
 * can recolor the app from Settings → Appearance. UT Austin is the default.
 */
import { useEffect } from 'react';
import { doc, getDoc, setDoc, onSnapshot } from 'firebase/firestore';
import { db } from './firebase.js';
import { useAuth } from './auth.js';

export interface SfpTheme {
  /** Buttons, brand mark, avatar, active states. */
  primary: string;
  /** Left sidebar background (should be dark for legible light text). */
  sidebar: string;
  /** App canvas background behind content cards. */
  canvas: string;
}

export const DEFAULT_THEME: SfpTheme = {
  primary: '#BF5700', // UT burnt orange
  sidebar: '#333F48', // UT charcoal
  canvas: '#FAFAF9', // near-white
};

export const PRESETS: Record<string, SfpTheme> = {
  'UT Austin': { primary: '#BF5700', sidebar: '#333F48', canvas: '#FAFAF9' },
  'SFP Classic': { primary: '#1F3357', sidebar: '#1A1D24', canvas: '#F4EFE6' },
  Forest: { primary: '#2F6F4F', sidebar: '#1E2A24', canvas: '#F2F5F1' },
  Slate: { primary: '#3B5BA5', sidebar: '#1E293B', canvas: '#F4F6F9' },
  Plum: { primary: '#7C3A66', sidebar: '#2A1F2C', canvas: '#F7F3F6' },
};

const LOCAL_KEY = 'sfp.theme.v1';
const DEMO_MODE = import.meta.env.VITE_SFP_DEMO_MODE === 'true';

/** Blend a hex toward black (pct<0) or white (pct>0). pct in [-1,1]. */
function shade(hex: string, pct: number): string {
  const n = parseInt(hex.replace('#', ''), 16);
  if (Number.isNaN(n)) return hex;
  let r = (n >> 16) & 255;
  let g = (n >> 8) & 255;
  let b = n & 255;
  const t = pct < 0 ? 0 : 255;
  const p = Math.abs(pct);
  r = Math.round((t - r) * p + r);
  g = Math.round((t - g) * p + g);
  b = Math.round((t - b) * p + b);
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}

/** Write the theme to the document's CSS variables — takes effect immediately. */
export function applyTheme(t: SfpTheme): void {
  const s = document.documentElement.style;
  s.setProperty('--sfp-primary', t.primary);
  s.setProperty('--sfp-primary-hover', shade(t.primary, -0.22));
  s.setProperty('--sfp-sidebar', t.sidebar);
  s.setProperty('--sfp-sidebar-hover', shade(t.sidebar, 0.12));
  s.setProperty('--sfp-sidebar-active', shade(t.sidebar, 0.2));
  s.setProperty('--sfp-canvas', t.canvas);
}

function readLocal(): SfpTheme | null {
  try {
    const raw = localStorage.getItem(LOCAL_KEY);
    return raw ? (JSON.parse(raw) as SfpTheme) : null;
  } catch {
    return null;
  }
}

export async function loadTheme(uid: string | null): Promise<SfpTheme> {
  if (DEMO_MODE || !uid) return readLocal() ?? DEFAULT_THEME;
  try {
    const snap = await getDoc(doc(db, 'userSettings', uid));
    const t = snap.exists() ? (snap.data() as any).theme : null;
    return t ?? readLocal() ?? DEFAULT_THEME;
  } catch {
    return readLocal() ?? DEFAULT_THEME;
  }
}

export async function saveTheme(uid: string | null, t: SfpTheme): Promise<void> {
  try {
    localStorage.setItem(LOCAL_KEY, JSON.stringify(t));
  } catch {
    /* ignore */
  }
  applyTheme(t);
  if (DEMO_MODE || !uid) return;
  await setDoc(doc(db, 'userSettings', uid), { theme: t }, { merge: true });
}

/**
 * App-wide theme application. Mount once (in Layout): applies the cached theme
 * instantly, then live-syncs from the user's saved theme in Firestore.
 */
export function useApplyTheme(): void {
  const { user } = useAuth();
  useEffect(() => {
    applyTheme(readLocal() ?? DEFAULT_THEME);
    if (DEMO_MODE || !user) return;
    const ref = doc(db, 'userSettings', user.uid);
    const unsub = onSnapshot(ref, (snap) => {
      const t = snap.exists() ? (snap.data() as any).theme : null;
      if (t) {
        applyTheme(t as SfpTheme);
        try {
          localStorage.setItem(LOCAL_KEY, JSON.stringify(t));
        } catch {
          /* ignore */
        }
      }
    });
    return unsub;
  }, [user]);
}
